import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Alert } from 'react-native';
import {
  Text,
  TextInput,
  Card,
  Button,
  IconButton,
  useTheme,
} from 'react-native-paper';
import { useFieldArray, useFormContext, Controller } from 'react-hook-form';
import {
  DraggableList,
  DraggableListRenderItemInfo,
  ScaleDecorator,
} from '../../common/DraggableList';
import { ImageUpload } from './ImageUpload';

interface StepItem {
  id: string;
  step_number: number;
  title: string;
  description: string;
  duration_seconds?: number;
  temperature_celsius?: number;
  notes?: string;
  image_url?: string;
}

const NumberInput = ({
  value,
  onChange,
  onBlur,
  ...props
}: Omit<React.ComponentProps<typeof TextInput>, 'value' | 'onChangeText'> & {
  value?: number | null;
  onChange: (value?: number) => void;
}) => {
  const [internalValue, setInternalValue] = useState(value?.toString() ?? '');
  const [isFocused, setIsFocused] = useState(false);

  // Only sync external value when not focused
  useEffect(() => {
    if (!isFocused) {
      if (value === undefined || value === null) {
        setInternalValue('');
      } else {
        setInternalValue(value.toString());
      }
    }
  }, [value, isFocused]);

  const handleChangeText = (text: string) => {
    const cleanText = text.replace(/[^0-9]/g, '');
    setInternalValue(cleanText);

    if (cleanText === '') {
      onChange(undefined);
    } else {
      const val = parseInt(cleanText, 10);
      if (!isNaN(val)) onChange(val);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e: any) => {
    setIsFocused(false);
    onBlur?.(e);

    // Sync with external value on blur
    if (value !== undefined && value !== null) {
      setInternalValue(value.toString());
    } else {
      setInternalValue('');
    }
  };

  return (
    <TextInput
      {...props}
      value={internalValue}
      onChangeText={handleChangeText}
      onFocus={handleFocus}
      onBlur={handleBlur}
      keyboardType='numeric'
    />
  );
};

export function RecipeSteps() {
  const theme = useTheme();
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'steps',
  });

  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());

  const addStep = () => {
    const newStepNumber = fields.length + 1;
    append({
      step_number: newStepNumber,
      title: '',
      description: '',
      duration_seconds: undefined,
      temperature_celsius: undefined,
      notes: '',
      image_url: '',
    });
    // Auto-expand new step
    setExpandedSteps(prev => new Set([...prev, fields.length]));
  };

  const removeStep = (index: number) => {
    if (fields.length === 1) {
      Alert.alert('Cannot Remove', 'At least one step is required.');
      return;
    }

    Alert.alert('Remove Step', 'Are you sure you want to remove this step?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: () => {
          // Create a temporary array without the removed step
          const currentSteps = [...fields];
          currentSteps.splice(index, 1);

          // Remove the step from the field array
          remove(index);

          // Update step numbers for remaining steps using the temporary array
          currentSteps.forEach((step, stepIndex) => {
            setValue(`steps.${stepIndex}.step_number`, stepIndex + 1);
          });

          // Update expanded steps
          const newExpanded = new Set(expandedSteps);
          newExpanded.delete(index);
          setExpandedSteps(newExpanded);
        },
      },
    ]);
  };

  const toggleStepExpansion = (index: number) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSteps(newExpanded);
  };

  const onDragEnd = ({
    data,
    from,
    to,
  }: {
    data: StepItem[];
    from: number;
    to: number;
  }) => {
    if (from === to) return;

    // Create a temporary array with the new order
    const currentSteps = [...data];
    const [movedStep] = currentSteps.splice(from, 1);
    if (movedStep) {
      currentSteps.splice(to, 0, movedStep);
    }

    // Update the field array with the new order
    move(from, to);

    // Update step numbers synchronously using the temporary array
    currentSteps.forEach((step, index) => {
      setValue(`steps.${index}.step_number`, index + 1);
    });
  };

  const renderStepItem = ({
    item,
    index,
    drag,
    isActive,
  }: DraggableListRenderItemInfo<StepItem>) => {
    // Safety check - if index is invalid, don't render
    if (index === -1 || index === undefined) {
      return null;
    }

    const isExpanded =
      expandedSteps.has(index) || !watch(`steps.${index}.title`);

    return (
      <ScaleDecorator>
        <Card
          style={[
            styles.stepCard,
            isActive && { backgroundColor: theme.colors.primaryContainer },
          ]}
        >
          {/* Step Header */}
          <View style={styles.stepHeader}>
            <View style={styles.stepHeaderLeft}>
              {/* Drag Handle */}
              <IconButton
                icon='drag-horizontal-variant'
                size={20}
                onLongPress={drag}
                iconColor={theme.colors.onSurfaceVariant}
              />

              {/* Step Number */}
              <View
                style={[
                  styles.stepNumber,
                  { backgroundColor: theme.colors.primary },
                ]}
              >
                <Text
                  variant='labelMedium'
                  style={{ color: theme.colors.onPrimary }}
                >
                  {index + 1}
                </Text>
              </View>

              {/* Step Title */}
              <View style={styles.stepTitleContainer}>
                <Controller
                  control={control}
                  name={`steps.${index}.title`}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextInput
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      placeholder={`Step ${index + 1} title`}
                      dense
                      style={styles.stepTitleInput}
                      error={!!errors.steps?.[index]?.title}
                    />
                  )}
                />
              </View>
            </View>

            <View style={styles.stepHeaderRight}>
              <IconButton
                icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                size={20}
                onPress={() => toggleStepExpansion(index)}
              />
              <IconButton
                icon='delete'
                size={20}
                onPress={() => removeStep(index)}
                disabled={fields.length === 1}
                iconColor={
                  fields.length === 1
                    ? theme.colors.outline
                    : theme.colors.error
                }
              />
            </View>
          </View>

          {/* Step Details (Expandable) */}
          {isExpanded && (
            <Card.Content style={styles.stepContent}>
              {/* Description */}
              <Controller
                control={control}
                name={`steps.${index}.description`}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    label='Instructions *'
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    multiline
                    numberOfLines={3}
                    error={!!errors.steps?.[index]?.description}
                    style={styles.input}
                    placeholder='Describe what to do in this step...'
                  />
                )}
              />
              {errors.steps?.[index]?.description && (
                <Text
                  variant='bodySmall'
                  style={[styles.errorText, { color: theme.colors.error }]}
                >
                  {errors.steps[index].description.message as string}
                </Text>
              )}

              {/* Duration and Temperature */}
              <View style={styles.row}>
                <View style={styles.halfWidth}>
                  <Controller
                    control={control}
                    name={`steps.${index}.duration_seconds`}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <NumberInput
                        label='Duration (seconds)'
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        style={styles.input}
                        placeholder='30'
                      />
                    )}
                  />
                </View>
                <View style={styles.halfWidth}>
                  <Controller
                    control={control}
                    name={`steps.${index}.temperature_celsius`}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <NumberInput
                        label='Temperature (°C)'
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        style={styles.input}
                        placeholder='85'
                      />
                    )}
                  />
                </View>
              </View>

              {/* Notes */}
              <Controller
                control={control}
                name={`steps.${index}.notes`}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    label='Notes (optional)'
                    value={value || ''}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    multiline
                    numberOfLines={2}
                    style={styles.input}
                    placeholder='Additional tips or important details...'
                  />
                )}
              />

              {/* Image Upload */}
              <Text variant='titleSmall' style={styles.imageLabel}>
                Step Image (optional)
              </Text>
              <ImageUpload
                onImageUploaded={url =>
                  setValue(`steps.${index}.image_url`, url)
                }
                currentImage={watch(`steps.${index}.image_url`)}
                onImageRemoved={() => setValue(`steps.${index}.image_url`, '')}
              />
            </Card.Content>
          )}
        </Card>
      </ScaleDecorator>
    );
  };

  return (
    <View style={styles.container}>
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.header}>
            <Text variant='headlineSmall' style={styles.sectionTitle}>
              Recipe Steps
            </Text>
            <Button
              mode='contained'
              onPress={addStep}
              icon='plus'
              style={styles.addButton}
              contentStyle={styles.addButtonContent}
              labelStyle={styles.addButtonLabel}
            >
              Add Step
            </Button>
          </View>

          {fields.length > 0 ? (
            <DraggableList<StepItem>
              data={
                fields.map(field => ({ ...field, id: field.id })) as StepItem[]
              }
              onDragEnd={onDragEnd}
              keyExtractor={item => item.id}
              renderItem={renderStepItem}
            />
          ) : (
            <View style={styles.emptyState}>
              <Text
                variant='bodyMedium'
                style={[
                  styles.emptyText,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                No steps added yet
              </Text>
              <Button
                mode='outlined'
                onPress={addStep}
                icon='plus'
                style={[styles.emptyButton, styles.addButton]}
                contentStyle={styles.addButtonContent}
                labelStyle={styles.addButtonLabel}
              >
                Add Your First Step
              </Button>
            </View>
          )}

          {errors.steps && (
            <Text
              variant='bodySmall'
              style={[styles.errorText, { color: theme.colors.error }]}
            >
              {errors.steps.message as string}
            </Text>
          )}
        </Card.Content>
      </Card>

      {/* Tips */}
      <Card
        style={[
          styles.card,
          styles.tipsCard,
          { backgroundColor: theme.colors.primaryContainer },
        ]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={[
              styles.tipsTitle,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            Tips for Recipe Steps
          </Text>
          <Text
            variant='bodySmall'
            style={[
              styles.tipsText,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            • Be specific about timing and temperatures{'\n'}• Include visual
            cues (e.g., "until golden brown"){'\n'}• Mention what to look for or
            listen for{'\n'}• Add photos to show the expected result{'\n'}• Use
            long press and drag to reorder steps
          </Text>
        </Card.Content>
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingBottom: 24,
  },
  card: {
    marginBottom: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontWeight: 'bold',
  },
  stepCard: {
    marginBottom: 12,
  },
  stepHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  stepHeaderLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  stepTitleContainer: {
    flex: 1,
  },
  stepTitleInput: {
    backgroundColor: 'transparent',
  },
  stepContent: {
    paddingTop: 0,
  },
  input: {
    marginBottom: 8,
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  halfWidth: {
    flex: 1,
  },
  imageLabel: {
    marginTop: 8,
    marginBottom: 8,
  },
  errorText: {
    marginTop: 4,
    marginBottom: 8,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyText: {
    marginBottom: 16,
  },
  emptyButton: {
    minWidth: 200,
  },
  addButton: {
    borderRadius: 20,
  },
  addButtonContent: {
    height: 40,
    paddingHorizontal: 16,
  },
  addButtonLabel: {
    fontSize: 14,
    lineHeight: 20,
  },
  tipsCard: {
    marginTop: 8,
  },
  tipsTitle: {
    marginBottom: 8,
    fontWeight: 'bold',
  },
  tipsText: {
    lineHeight: 20,
  },
});
